import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { LTReportData } from '@/types/ltTypes'
import { drawMethodTitle, getNdtResultBodyStyles, getNdtResultTableOptions } from './pdfTitle'
import {
  addNdtFooter,
  formatDateDDMMYYYY,
  getCertificateNumber,
  getFullName,
  getLogoDataUrl,
  getSignature,
  NDT_TABLE_OPTIONS,
} from './ndtPdfShared'

export async function generatePDF(data: LTReportData): Promise<Blob> {
  const verifierName = (data.approvedBy || data.verifiedBy || '').trim()
  const verifierDate = data.approvedAt || data.testDate

  const [inspectorSignature, logoDataUrl, certificationNumber] = await Promise.all([
    getSignature(data.issuedBy),
    getLogoDataUrl(),
    getCertificateNumber(data.issuedBy),
  ])
  const approverSignature = verifierName ? await getSignature(verifierName) : null

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.width
  const pageHeight = doc.internal.pageSize.height
  const margin = 15

  drawMethodTitle(doc, 'Load Test', margin, margin, { trailing: 'Report' })
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12)
  }

  let yPos = margin + 15
  const tableOptions = { ...NDT_TABLE_OPTIONS, margin: { left: margin, right: margin } }
  const leftColumnWidth = pageWidth * 0.7 - margin
  const rightColumnWidth = pageWidth * 0.3 - margin
  const lastY = () => (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY
  const third = (pageWidth - 2 * margin) / 3

  autoTable(doc, {
    startY: yPos,
    head: [['TEST DATE', 'LOCATION', 'REPORT NO.']],
    body: [[
      formatDateDDMMYYYY(data.testDate),
      data.testSite || 'Bergen',
      `LT-${data.reportNumber ?? 'N/A'}.${data.sequenceNumber || '001'}`,
    ]],
    ...tableOptions,
    columnStyles: {
      0: { cellWidth: third },
      1: { cellWidth: third },
      2: { cellWidth: third },
    },
  })
  yPos = lastY()

  const objectIds = (data.objects || []).map((obj) => obj.trim()).filter(Boolean)
  const objectName = (data.objectDescription || data.objectType || '').trim()
  const objectCell = objectIds.length > 0
    ? `Objekt: ${objectName || '---'}, ID: ${objectIds.join(', ')}`
    : (objectName || '---')

  autoTable(doc, {
    startY: yPos,
    head: [['OBJECT']],
    body: [[objectCell]],
    ...tableOptions,
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['DRAWING', 'PERFORMING COMPANY/CERT NO']],
    body: [['---', `STMI / ${certificationNumber || '---'}`]],
    ...tableOptions,
    columnStyles: {
      0: { cellWidth: leftColumnWidth, overflow: 'linebreak' },
      1: { cellWidth: rightColumnWidth, overflow: 'linebreak' },
    },
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['SURFACE CONDITIONS', 'EXTENT OF EXAMINATION']],
    body: [['Cleaned', `${data.extentOfExamination}%`]],
    ...tableOptions,
    columnStyles: { 0: { cellWidth: leftColumnWidth }, 1: { cellWidth: rightColumnWidth } },
  })
  yPos = lastY()

  const equipmentUsed = data.equipmentSettings?.equipmentUsed || '---'
  const equipmentSerialNo = (data.equipmentSettings?.equipmentSerialNo || '').trim()
  const equipmentCell =
    equipmentSerialNo && equipmentSerialNo !== '---'
      ? `${equipmentUsed} SN ${equipmentSerialNo}`
      : equipmentUsed

  autoTable(doc, {
    startY: yPos,
    head: [['EQUIPMENT USED']],
    body: [[equipmentCell]],
    ...tableOptions,
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['TEST PROCEDURE']],
    body: [[data.testProcedure || 'STM - Load Testing']],
    ...tableOptions,
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['ACCEPTANCE']],
    body: [['No signs of deflection, deformation or elongation during or after load test']],
    ...tableOptions,
  })
  yPos = lastY()

  autoTable(doc, {
    startY: yPos,
    head: [['TEST LOAD (t)', 'TEST DURATION (m)']],
    body: [[
      data.testLoad ? `${data.testLoad} tons` : '---',
      data.testDuration ? `${data.testDuration} minutes` : '---',
    ]],
    ...tableOptions,
    columnStyles: { 0: { cellWidth: leftColumnWidth }, 1: { cellWidth: rightColumnWidth } },
  })
  yPos = lastY()

  const remainingSpace = pageHeight - 2 * margin - yPos - 40
  const summaryObjectLines = objectIds.length > 0
    ? objectIds.map((id) => `- ${objectName ? `${objectName} (${id})` : id}`).join('\n')
    : (objectName ? `- ${objectName}` : '---')

  let summaryText = 'STMI has witnessed a static load test of:\n\n' + summaryObjectLines
  if (data.comments) {
    summaryText += '\n\nCOMMENTS:\n\n' + data.comments
  }

  autoTable(doc, {
    startY: yPos,
    head: [['SUMMARY']],
    body: [[summaryText]],
    ...tableOptions,
    bodyStyles: { fontSize: 8, minCellHeight: Math.max(remainingSpace, 40) },
  })
  yPos = lastY()

  const isRejected = data.status === 'Not Accepted'
  autoTable(doc, {
    startY: yPos,
    head: [['RESULT']],
    body: [[isRejected ? 'REJECTED' : 'ACCEPTED']],
    ...getNdtResultTableOptions(margin),
    bodyStyles: getNdtResultBodyStyles(isRejected),
  })
  yPos = lastY()

  const signatureWidth = (pageWidth - 2 * margin) / 2
  const hasVerifier = !!verifierName
  doc.setDrawColor(0)
  doc.setLineWidth(0.1)

  if (hasVerifier) {
    doc.rect(margin, yPos, signatureWidth, 25)
    doc.rect(margin + signatureWidth, yPos, signatureWidth, 25)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.text('ISSUED BY', margin + signatureWidth / 2, yPos + 4, { align: 'center' })
    doc.text('VERIFIED BY', margin + signatureWidth + signatureWidth / 2, yPos + 4, { align: 'center' })
  } else {
    doc.rect(margin, yPos, pageWidth - 2 * margin, 25)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.text('ISSUED BY', margin + (pageWidth - 2 * margin) / 2, yPos + 4, { align: 'center' })
  }

  if (inspectorSignature) {
    doc.addImage(inspectorSignature, 'PNG', hasVerifier ? margin + signatureWidth / 2 - 20 : pageWidth / 2 - 20, yPos + 8, 40, 10)
  }
  if (approverSignature && hasVerifier) {
    doc.addImage(approverSignature, 'PNG', margin + signatureWidth + signatureWidth / 2 - 20, yPos + 8, 40, 10)
  }

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  const signatureSite = data.testSite || 'Bergen'
  if (hasVerifier) {
    doc.text(`${getFullName(data.issuedBy)} / ${formatDateDDMMYYYY(data.testDate)} / ${signatureSite}`, margin + signatureWidth / 2, yPos + 22, { align: 'center' })
    doc.text(`${getFullName(verifierName)} / ${formatDateDDMMYYYY(verifierDate)} / ${signatureSite}`, margin + signatureWidth + signatureWidth / 2, yPos + 22, { align: 'center' })
  } else {
    doc.text(`${getFullName(data.issuedBy)} / ${formatDateDDMMYYYY(data.testDate)} / ${signatureSite}`, pageWidth / 2, yPos + 22, { align: 'center' })
  }

  if (data.attachedImage) {
    doc.addPage()
    drawMethodTitle(doc, 'Load Test', margin, margin, { trailing: 'Report', suffix: ' - ATTACHED IMAGE' })
    if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12)
    doc.setDrawColor(0)
    doc.setLineWidth(0.5)
    doc.rect(margin, margin + 10, pageWidth - 2 * margin, pageHeight - 2 * margin - 10)
    try {
      const availableWidth = pageWidth - 4 * margin
      const availableHeight = pageHeight - 4 * margin - 20
      doc.addImage(
        data.attachedImage,
        'JPEG',
        margin + (availableWidth - availableWidth * 0.8) / 2 + margin,
        margin + 20 + (availableHeight - availableHeight * 0.8) / 2 + margin,
        availableWidth * 0.8,
        availableHeight * 0.8,
      )
    } catch {
      doc.setFontSize(12)
      doc.text('Error loading attached image', pageWidth / 2, pageHeight / 2, { align: 'center' })
    }
  }

  addNdtFooter(doc)
  return doc.output('blob')
}

export const generateLTReport = generatePDF
